/* eslint-disable */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

// ── Generate the self-contained index.html shell ──────────────────────────────
const buildDownloadHtml = (apiBaseUrl, companycif, slug) => `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Loading…</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    body { margin: 0; font-family: system-ui, sans-serif; }

    #__wb_loader__ {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      gap: 16px;
      color: #6c757d;
      font-size: 15px;
    }
    #__wb_loader__ .spinner {
      width: 40px; height: 40px;
      border: 4px solid #e9ecef;
      border-top-color: #0d6efd;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    #__wb_error__ {
      display: none;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      gap: 12px;
      color: #dc3545;
      font-size: 15px;
      text-align: center;
      padding: 24px;
    }
  </style>
</head>
<body>

  <div id="__wb_loader__">
    <div class="spinner"></div>
    <span>Loading page…</span>
  </div>

  <div id="__wb_error__">
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
      <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
    </svg>
    <strong id="__wb_error_msg__">Failed to load page.</strong>
    <small>Make sure the page is published and the server is reachable.</small>
  </div>

  <div id="__wb_root__"></div>

  <script>
    var API_BASE   = "${apiBaseUrl}";
    var COMPANY_CIF = "${companycif}";
    var SLUG        = "${slug}";

    function showError(msg) {
      document.getElementById("__wb_loader__").style.display = "none";
      var errEl = document.getElementById("__wb_error__");
      errEl.style.display = "flex";
      if (msg) document.getElementById("__wb_error_msg__").textContent = msg;
    }

    async function init() {
      try {
        var apiUrl = API_BASE + "/builder/pages/public?cif=" + encodeURIComponent(COMPANY_CIF) + "&slug=" + encodeURIComponent(SLUG);
        var res = await fetch(apiUrl);

        if (!res.ok) { showError("Server returned " + res.status); return; }

        var data = await res.json();

        if (!data.status) { showError(data.message || "Page not found or not published."); return; }

        var page = data.data;

        // Inject page title
        document.title = page.name || SLUG;

        // Inject CSS
        if (page.css) {
          var style = document.createElement("style");
          style.textContent = page.css;
          document.head.appendChild(style);
        }

        // Inject HTML
        document.getElementById("__wb_root__").innerHTML = page.html || "";

        // Hide loader
        document.getElementById("__wb_loader__").style.display = "none";

        // Inject JS (last, so DOM is ready)
        if (page.js) {
          var script = document.createElement("script");
          script.textContent = page.js;
          document.body.appendChild(script);
        }

      } catch (err) {
        showError("Network error: " + err.message);
      }
    }

    init();
  </script>

</body>
</html>`;

// ── FAQ items ─────────────────────────────────────────────────────────────────
const buildFaq = (companycif, slug, apiBaseUrl, onDownload) => (t) => [
  {
    id: "faq-domain",
    icon: "language",
    question: "How do I view this page on my domain or subdomain?",
    cta: (
      <button
        className="btn btn-sm btn-primary d-flex align-items-center gap-1 px-3"
        onClick={onDownload}
        title={t("Download a ready-to-upload index.html for this page")}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>download</span>
        {t("Download Page")}
      </button>
    ),
    answer: (
      <>
        <p className="text-muted small mb-2">
          {t("Once your page is published, you can serve it at any URL. The easiest way is to download the ready-made")} <strong>index.html</strong> {t("shell (button above) and upload it to your server or CDN. Here's the full setup:")}
        </p>

        <p className="small fw-semibold mb-1">{t("Option A — Download & upload (simplest)")}</p>
        <ol className="small text-muted ps-3 mb-3" style={{ lineHeight: 1.8 }}>
          <li>{t("Click")} <strong>{t("Download Page")}</strong> {t("above. You'll get an")} <code>index.html</code> {t("with your Company CIF")} (<code>{companycif || "—"}</code>) {t("and slug")} (<code>{slug || "—"}</code>) {t("already baked in.")}</li>
          <li>{t("Upload it to any static hosting (Nginx, Apache, Cloudflare Pages, S3, Vercel, etc.) at any path — e.g.")} <code>/var/www/mysite/index.html</code>.</li>
          <li>{t("When a visitor opens the page, it calls the public API, fetches the latest published HTML/CSS/JS, and renders it — no build step needed.")}</li>
          <li>{t("Re-publish the page in the editor whenever you want to update the live content. The uploaded file never needs to change.")}</li>
        </ol>

        <p className="small fw-semibold mb-1">{t("Option B — Call the public API yourself")}</p>
        <p className="text-muted small mb-1">
          {t("The platform exposes a public endpoint (no login required):")}
        </p>
        <div className="bg-light border rounded-2 px-3 py-2 mb-3 small font-monospace text-dark" style={{ wordBreak: "break-all" }}>
          GET {apiBaseUrl}/builder/pages/public?cif={companycif || "{companycif}"}&slug={slug || "{slug}"}
        </div>
        <p className="text-muted small mb-3">
          {t("Returns")} <code>{"{ status, data: { name, html, css, js, slug } }"}</code>. {t("Use it in your own SSR, iframe, or frontend framework.")}
        </p>

        <p className="small fw-semibold mb-1">{t("Option C — Point your own domain")}</p>
        <ol className="small text-muted ps-3 mb-2" style={{ lineHeight: 1.8 }}>
          <li>{t("In your DNS, add a CNAME or A record pointing your domain/subdomain to this server.")}</li>
          <li>{t("Configure a reverse proxy (Nginx / Apache / Caddy) to forward requests to the public API above.")}</li>
        </ol>

        <div className="alert alert-primary py-2 px-3 small mb-0 rounded-3" role="alert">
          <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: 16 }}>tips_and_updates</span>
          {t("Tip: Option A is the fastest — download once, upload anywhere, update content by re-publishing in the editor.")}
        </div>
      </>
    ),
  },
  {
    id: "faq-images",
    icon: "image",
    question: "How do I replace image placeholders with my own images?",
    answer: (
      <>
        <p className="text-muted small mb-2">
          {t("When a page is generated, every image area is created as a numbered placeholder (e.g.")} <strong>{t("Image Placeholder 1")}</strong>{t(") so the layout is preserved. Replace them in two steps:")}
        </p>
        <p className="small fw-semibold mb-1">{t("Step 1 — Upload your image and get its URL")}</p>
        <ol className="small text-muted ps-3 mb-3" style={{ lineHeight: 1.8 }}>
          <li>{t("Go to any media/file upload section in this platform and upload your image.")}</li>
          <li>{t("After uploading, copy the full image URL (e.g.")} <code>/uploads/my-hero.jpg</code>{t(").")}</li>
        </ol>
        <p className="small fw-semibold mb-1">{t("Step 2 — Replace the placeholder via chat")}</p>
        <ol className="small text-muted ps-3 mb-3" style={{ lineHeight: 1.8 }}>
          <li>{t("Look at the preview — each placeholder box shows its number (e.g. 📷 Image Placeholder 2).")}</li>
          <li>
            {t("In the chat box, type:")}
            <div className="bg-light border rounded-2 px-3 py-2 my-1 small text-dark">
              {t("replace placeholder 2 with /uploads/my-hero.jpg")}
            </div>
          </li>
          <li>{t("The AI will update the HTML and the preview will show your actual image.")}</li>
        </ol>
        <div className="alert alert-success py-2 px-3 small mb-0 rounded-3" role="alert">
          <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: 16 }}>check_circle</span>
          {t("You can replace all placeholders one by one in a single chat session. The page is saved automatically after each replacement.")}
        </div>
      </>
    ),
  },
  {
    id: "faq-versions",
    icon: "history",
    question: "How do version history and rollback work?",
    answer: (
      <>
        <p className="text-muted small mb-2">
          {t("Every time the AI updates your page, the previous version is saved automatically (up to 10 versions are kept).")}
        </p>
        <ol className="small text-muted ps-3 mb-2" style={{ lineHeight: 1.8 }}>
          <li>{t("Click the")} <strong>{t("Versions")}</strong> {t("button in the toolbar to see all saved versions.")}</li>
          <li>{t("Each version shows the date and time it was created.")}</li>
          <li>{t("Click Rollback next to any version to restore the page to that state.")}</li>
        </ol>
        <div className="alert alert-warning py-2 px-3 small mb-0 rounded-3" role="alert">
          <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: 16 }}>warning</span>
          {t("Rolling back overwrites the current page. The current state is saved as a version before rollback, so it can be recovered.")}
        </div>
      </>
    ),
  },
];

// ── Component ─────────────────────────────────────────────────────────────────
const WbHelpOffcanvas = ({ companycif = "", slug = "", apiBaseUrl = "" }) => {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState("faq-domain");

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  const handleDownload = () => {
    const html = buildDownloadHtml(apiBaseUrl, companycif, slug);
    const blob = new Blob([html], { type: "text/html" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${slug || "page"}.html`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const faqItems = buildFaq(companycif, slug, apiBaseUrl, handleDownload)(t);

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="wbHelpOffcanvas"
      aria-labelledby="wbHelpOffcanvasLabel"
    >
      <div className="offcanvas-header bg-primary text-white p-4 pb-3 border-bottom border-gray-300">
        <h5 className="offcanvas-title fw-semibold text-white" id="wbHelpOffcanvasLabel">
          <span className="material-symbols-outlined me-2 align-middle">help</span>
          {t("Website Builder — Help")}
        </h5>
        <button
          type="button"
          className="btn-close btn-close-white"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        />
      </div>

      <div className="offcanvas-body px-4 py-3">
        <div className="accordion accordion-flush" id="wbHelpAccordion">
          {faqItems.map((item) => {
            const isOpen = openId === item.id;
            return (
              <div className="accordion-item border rounded-3 mb-2" key={item.id}>
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button rounded-3 fw-semibold small gap-2 ${isOpen ? "" : "collapsed"}`}
                    type="button"
                    onClick={() => toggle(item.id)}
                    aria-expanded={isOpen}
                  >
                    <span
                      className="material-symbols-outlined text-primary flex-shrink-0"
                      style={{ fontSize: 20 }}
                    >
                      {item.icon}
                    </span>
                    <span className="flex-grow-1 text-start">{t(item.question)}</span>
                    {item.cta && isOpen && (
                      <span onClick={(e) => e.stopPropagation()}>{item.cta}</span>
                    )}
                  </button>
                </h2>
                <div className={`accordion-collapse collapse ${isOpen ? "show" : ""}`}>
                  <div className="accordion-body pt-2 pb-3">{item.answer}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WbHelpOffcanvas;
